import React from 'react';

interface PageMediaProps {
  imageUrl?: string;
  videoUrl?: string;
  mediaType?: 'image' | 'video';
  videoAutoplay?: boolean;
  alt?: string;
  className?: string;
}

// 유튜브 URL을 embed URL로 변환하는 함수
const getYoutubeEmbedUrl = (url: string): string => {
  if (!url) return '';

  // 이미 embed URL인 경우 그대로 반환
  if (url.includes('youtube.com/embed/')) {
    return url;
  }

  // 다양한 유튜브 URL 형식 처리
  let videoId = '';

  // youtube.com/watch?v=VIDEO_ID
  const watchMatch = url.match(/[?&]v=([^&]+)/);
  if (watchMatch) {
    videoId = watchMatch[1];
  }

  // youtu.be/VIDEO_ID
  const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
  if (shortMatch) {
    videoId = shortMatch[1];
  }

  // youtube.com/v/VIDEO_ID
  const vMatch = url.match(/youtube\.com\/v\/([^?&]+)/);
  if (vMatch) {
    videoId = vMatch[1];
  }

  if (videoId) {
    return `https://www.youtube.com/embed/${videoId}`;
  }

  return url; // 변환 실패 시 원본 반환
};

/**
 * 페이지 콘텐츠의 이미지 또는 영상을 표시하는 컴포넌트
 * media_type에 따라 이미지 또는 유튜브 영상을 렌더링합니다.
 */
const PageMedia: React.FC<PageMediaProps> = ({
  imageUrl,
  videoUrl,
  mediaType = 'image',
  videoAutoplay = false,
  alt = '페이지 미디어',
  className = ''
}) => {
  // 미디어가 없는 경우 렌더링하지 않음
  if (mediaType === 'image' && !imageUrl) return null;
  if (mediaType === 'video' && !videoUrl) return null;

  return (
    <div className={`mt-8 sm:mt-10 md:mt-12 ${className}`}>
      {mediaType === 'image' && imageUrl && (
        <img
          src={imageUrl}
          alt={alt}
          className="w-full max-w-4xl mx-auto rounded-xl sm:rounded-2xl shadow-lg"
        />
      )}

      {mediaType === 'video' && videoUrl && (
        <div className="w-full max-w-4xl mx-auto">
          <div className="relative aspect-video rounded-xl sm:rounded-2xl overflow-hidden shadow-lg">
            <iframe
              src={`${getYoutubeEmbedUrl(videoUrl)}${videoAutoplay ? '?autoplay=1&mute=1' : ''}`}
              title="영상"
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PageMedia;
